import React, { ChangeEvent, FC, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import OnboardingShell from "./OnboardingShell";
import placeholderImg from "images/placeholder-large.png";
import { useLocale } from "./i18n/locale";

export interface TenantInventoryPageProps {
  className?: string;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps?: number;
}

// The tenant may add up to 15 photos of their own (spec, step 5).
const MAX_TENANT_PHOTOS = 15;

// TODO: the cleaning photos come from the DMS — these are the pictures taken
// after the last cleaning. Placeholder until that contract exists.
const CLEANING_PHOTOS = Array.from({ length: 6 }, (_, i) => ({
  id: `cleaning-${i + 1}`,
  src: placeholderImg,
}));

const TenantInventoryPage: FC<TenantInventoryPageProps> = ({
  className = "",
  totalSteps = 7,
}) => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [comments, setComments] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);

  const remaining = MAX_TENANT_PHOTOS - photos.length;

  const handleAddPhotos = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    // Silently keeping only what fits would hide files the tenant picked, so
    // cap the input instead and let the counter show the limit.
    setPhotos((prev) => [...prev, ...chosen].slice(0, MAX_TENANT_PHOTOS));
  };

  const handleConfirm = () => {
    // TODO: submit the inventory (comments + photos) and capture the tenant's
    // signature, then advance to step 6.
    navigate("/tenant/insurance");
  };

  return (
    <div
      className={`nc-TenantInventoryPage ${className}`}
      data-nc-id="TenantInventoryPage"
    >
      <Helmet>
        <title>{t.inventory.documentTitle}</title>
      </Helmet>

      <OnboardingShell
        step={5}
        totalSteps={totalSteps}
        title={t.inventory.title}
        subtitle={t.inventory.subtitle}
        wide
      >
        <div className="grid gap-3 sm:grid-cols-[1.6fr_1fr] sm:items-start">
          <section className="rounded-2xl bg-white p-4 shadow-sm sm:shadow-none sm:p-0">
            <h2 className="text-[14px] font-semibold text-navy-900">
              {t.inventory.cleaningPhotos}
            </h2>
            <p className="mt-0.5 text-xs text-neutral-500 font-semibold ">
              {t.inventory.cleaningPhotosCaption}
            </p>

            <ul className="mt-3 grid w-full grid-cols-3 gap-2">
              {CLEANING_PHOTOS.map((photo) => (
                <li key={photo.id}>
                  <img
                    src={"\\logo\\serviceBtn1.png"}
                    alt=""
                    className="w-full h-auto aspect-[3/2] rounded-lg object-cover"
                  />
                </li>
              ))}
            </ul>

            {/* TODO: open the full cleaning-photo gallery. */}
            <button
              type="button"
              className="mt-3 flex w-full items-center justify-between gap-2 rounded-lg border border-neutral-200 py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-navy-900 transition-colors hover:border-primary-6000 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6000"
            >
            <span className="flex-1 whitespace-nowrap text-center">
              {t.inventory.viewAllPhotos}
            </span>
              <svg
                className="h-4 w-4 flex-none"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </button>
          </section>

          <section className="rounded-md border border-neutral-200 bg-white p-4">
            <h2 className="text-[14px] font-semibold text-navy-900">
              {t.inventory.yourCheck}
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-neutral-500 font-semibold">
              {t.inventory.yourCheckCaption}
            </p>

            <label htmlFor="inventory-comments" className="sr-only">
              {t.inventory.commentsLabel}
            </label>
            <textarea
              id="inventory-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder={t.inventory.commentsPlaceholder}
              className="mt-3 w-full resize-none rounded-xl border border-neutral-200 px-3 py-2.5 text-[13px] text-neutral-700 placeholder:text-neutral-400 focus:border-primary-6000 focus:outline-none focus:ring-1 focus:ring-primary-6000"
            />

            <p className="mt-4 text-xs font-semibold text-navy-900">
              {t.inventory.photosUpTo(MAX_TENANT_PHOTOS)}
            </p>

            {photos.length > 0 && (
              <ul className="mt-2 grid grid-cols-3 gap-2">
                {photos.map((photo, i) => (
                  <li key={`${photo.name}-${i}`}>
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={photo.name}
                      className="aspect-square w-full rounded-lg object-cover"
                    />
                  </li>
                ))}
              </ul>
            )}

            <label
              htmlFor="tenant-photos"
              className={`mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed py-6 text-xs font-medium transition-colors focus-within:ring-2 focus-within:ring-primary-6000 ${
                remaining > 0
                  ? "border-neutral-300 text-navy-900 font-semibold hover:border-primary-6000"
                  : "cursor-not-allowed border-neutral-200 text-neutral-300"
              }`}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              {remaining > 0
                ? t.inventory.addPhotos
                : t.inventory.limitReached(MAX_TENANT_PHOTOS)}
              <input
                id="tenant-photos"
                type="file"
                accept="image/jpeg,image/png"
                multiple
                disabled={remaining <= 0}
                className="sr-only"
                onChange={handleAddPhotos}
              />
            </label>
          </section>
        </div>

        <button
          type="button"
          onClick={handleConfirm}
          className="mt-4 flex w-full items-center justify-between gap-3 rounded-md bg-navy-800 px-6 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-800 focus-visible:ring-offset-2"
        >
          <span className="flex-1 text-center">{t.inventory.cta}</span>
          <svg
            className="h-5 w-5 flex-none"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h18m0 0l-7-7m7 7l-7 7" />
          </svg>
        </button>
      </OnboardingShell>
    </div>
  );
};

export default TenantInventoryPage;
